import { Link, Route, Switch, useLocation } from "wouter";
import { useScrollTopOnNavigate } from "../../hooks/useScrollTopOnNavigate";
import { Building2, DollarSign, LifeBuoy, LogOut, Megaphone, MonitorUp, Puzzle, ShieldCheck } from "lucide-react";
import { ClevisMark, cn } from "@clevis/shared-ui";
import { useAuth } from "../../hooks/useAuth";
import { AdminCustomers } from "./AdminCustomers";
import { AdminIntegrations } from "./AdminIntegrations";
import { AdminTenantDetail } from "./AdminTenantDetail";
import { AdminSupport } from "./AdminSupport";
import { AdminRemoteSupport } from "./AdminRemoteSupport";
import { AdminPricing } from "./AdminPricing";
import { AdminMessages } from "./AdminMessages";
import { TwoStepGate, TwoStepNotice } from "../../components/TwoStepGate";
import { TwoStepSetup } from "../../components/TwoStepSetup";

const nav = [
  { href: "/", label: "Customers", icon: Building2 },
  { href: "/support", label: "Support", icon: LifeBuoy },
  { href: "/remote", label: "Remote Support", icon: MonitorUp },
  { href: "/pricing", label: "Pricing & Plans", icon: DollarSign },
  { href: "/messages", label: "App Messages", icon: Megaphone },
  { href: "/integrations", label: "Integrations", icon: Puzzle },
  { href: "/security", label: "My sign-in", icon: ShieldCheck },
];

export function AdminShell() {
  const { user, signOut } = useAuth();
  const [location] = useLocation();
  useScrollTopOnNavigate();

  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 flex h-screen w-56 shrink-0 flex-col self-start overflow-y-auto bg-ink text-white">
        <div className="flex items-center gap-2 px-4 py-4">
          <ClevisMark size={28} body="#F4F1EB" />
          <div>
            <div className="text-sm font-bold leading-tight">Clevis</div>
            <div className="text-[10px] uppercase tracking-wide text-steel-light">Master Admin</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-2">
          {nav.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-steel-light hover:bg-white/10 hover:text-white",
                location === item.href && "bg-white/10 text-white",
              )}
            >
              <item.icon size={16} />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="mb-2 truncate text-xs text-steel-light">{user?.email}</div>
          <button
            onClick={() => void signOut()}
            className="flex items-center gap-2 text-xs text-steel-light hover:text-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 space-y-4 p-6">
        <TwoStepGate>
        <TwoStepNotice href="/security" />
        <Switch>
          <Route path="/security">
            <div className="mx-auto max-w-xl space-y-3">
              <h1 className="text-lg font-bold">My sign-in</h1>
              <TwoStepSetup />
            </div>
          </Route>
          <Route path="/integrations" component={AdminIntegrations} />
          <Route path="/support" component={AdminSupport} />
          <Route path="/remote" component={AdminRemoteSupport} />
          <Route path="/pricing" component={AdminPricing} />
          <Route path="/messages" component={AdminMessages} />
          <Route path="/customers/:id" component={AdminTenantDetail} />
          <Route component={AdminCustomers} />
        </Switch>
        </TwoStepGate>
      </main>
    </div>
  );
}
